import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { CheckCircle2, AlertTriangle, Clock, FileEdit, RefreshCw } from "lucide-react";
import { cn } from "../../lib/cn";
import type { FinalReportEvent, StepReportEvent } from "../../lib/types";
import { StreamingText } from "../chat/StreamingText";
import { simplifyForUser } from "../../lib/simplifyForUser";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** Compact markdown renderer for step summaries with diff highlighting. */
function StepSummaryMarkdown({ text }: { text: string }) {
  return (
    <div className="text-[0.92em] leading-relaxed prose-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="my-1">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-fg">{children}</strong>,
          code: ({ className: cls, children, ...props }: any) => {
            // react-markdown v9 убрал prop `inline` — определяем сами.
            const raw = String(children ?? "");
            const isBlock = /language-/.test(cls || "") || raw.includes("\n");
            if (!isBlock) {
              return (
                <code className="px-1 py-0.5 rounded bg-bg-subtle text-accent-400 font-mono text-[0.88em] border border-border/50">
                  {children}
                </code>
              );
            }
            const lang = (cls || "").replace("language-", "").toLowerCase();
            const codeStr = String(children).replace(/\n$/, "");
            if (lang === "diff") {
              return <DiffBlock code={codeStr} />;
            }
            return (
              <pre className="my-1.5 p-2 rounded bg-bg-subtle border border-border/50 overflow-x-auto font-mono text-[0.85em]">
                <code>{children}</code>
              </pre>
            );
          },
          pre: ({ children }) => <>{children}</>,
          ul: ({ children }) => <ul className="list-disc ml-4 my-1 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal ml-4 my-1 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          h1: ({ children }) => <div className="font-semibold text-fg mt-1">{children}</div>,
          h2: ({ children }) => <div className="font-semibold text-fg mt-1">{children}</div>,
          h3: ({ children }) => <div className="font-semibold text-fg mt-1">{children}</div>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

/** Renders diff code block with green/red line highlighting. */
function DiffBlock({ code }: { code: string }) {
  const lines = code.split("\n");
  return (
    <pre className="my-1.5 p-2 rounded bg-bg-subtle border border-border/50 overflow-x-auto font-mono text-[0.85em]">
      {lines.map((line, i) => {
        let cls = "text-fg-muted";
        let bg = "";
        if (line.startsWith("+")) {
          cls = "text-success";
          bg = "bg-success/10";
        } else if (line.startsWith("-")) {
          cls = "text-danger";
          bg = "bg-danger/10";
        }
        return (
          <div key={i} className={cn("px-1 -mx-1 rounded-sm", bg, cls)}>
            {line}
          </div>
        );
      })}
    </pre>
  );
}

export function StepReport({
  report,
  onRetry,
}: {
  report: StepReportEvent;
  onRetry?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const Icon = report.status === "done" ? CheckCircle2 : AlertTriangle;
  const iconClass =
    report.status === "done"
      ? "text-success"
      : report.status === "partial"
        ? "text-warning"
        : "text-fg-subtle";
  return (
    <motion.div
      id={`step-report-${report.index}`}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className="card group relative overflow-hidden"
    >
      {/* Collapsible header — always visible */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center gap-3 text-left hover:bg-bg-subtle/50 transition-colors"
        aria-expanded={expanded}
      >
        <Icon className={cn("size-4 shrink-0", iconClass)} />
        <span className="text-fg-subtle text-small">{report.index}/{report.total}</span>
        <span className="font-medium text-fg text-small truncate flex-1">{report.title}</span>
        <span className="text-fg-subtle text-small inline-flex items-center gap-1 shrink-0">
          <Clock className="size-3" />
          {report.duration_sec.toFixed(1)}s
        </span>
        <motion.span
          animate={{ rotate: expanded ? 90 : 0 }}
          className="text-fg-subtle text-[0.7rem]"
        >
          ▶
        </motion.span>
      </button>

      {/* Expandable details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-0 space-y-1.5 border-t border-border/30">
              {report.summary && <StepSummaryMarkdown text={report.summary} />}
              {(report.tools_used.length > 0 || report.artifacts.length > 0) && (
                <div className="flex flex-wrap gap-1.5 text-small">
                  {report.tools_used.map((t, i) => (
                    <span key={`t-${i}`} className="px-2 py-0.5 rounded-full bg-bg-subtle text-fg-muted font-mono text-[0.8em]">
                      {t}
                    </span>
                  ))}
                  {report.artifacts.slice(0, 3).map((a, i) => (
                    <span key={`a-${i}`} className="px-2 py-0.5 rounded-full bg-bg-subtle text-fg-muted font-mono text-[0.8em] inline-flex items-center gap-1">
                      <FileEdit className="size-3" />
                      {a.length > 32 ? "…" + a.slice(-32) : a}
                    </span>
                  ))}
                </div>
              )}
              {report.errors.length > 0 && (
                <div className="text-small text-danger">{report.errors[0].slice(0, 200)}</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="absolute top-2 right-2 p-1.5 rounded-md bg-bg/80 hover:bg-bg-subtle text-fg-muted hover:text-accent-400 border border-border/40 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Переотправить задачу с этого шага"
          aria-label="Retry"
        >
          <RefreshCw className="size-3.5" />
        </button>
      )}
    </motion.div>
  );
}

export function FinalReport({ report, simple }: { report: FinalReportEvent; simple?: boolean }) {
  // В simple mode скрываем технические артефакты (Краткий обзор, /root/... пути)
  // из markdown финального отчёта — то же правило что и для message bubble.
  const markdown = simple ? simplifyForUser(report.markdown || "") : report.markdown;
  // Если в simple mode после фильтрации ничего не осталось — отчёт целиком технический,
  // не показываем его пользователю.
  if (simple && (!markdown || markdown.trim().length < 6)) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="card p-5 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-h3">{simple ? "Результат" : "Итоговый отчёт"}</h3>
        <div className="text-small text-fg-muted">
          {report.completed}/{report.total} выполнено
          {!simple && report.partial > 0 && `, ${report.partial} partial`}
        </div>
      </div>
      <StreamingText text={markdown} />
      {!simple && report.tool_gaps && report.tool_gaps.length > 0 && (
        <div className="mt-3 p-3 rounded-card bg-warning/10 border border-warning/30">
          <div className="text-small font-medium text-warning mb-1">
            Запросы на новые инструменты
          </div>
          <ul className="text-small text-fg-muted space-y-0.5">
            {report.tool_gaps.map((g) => (
              <li key={g.tool_name}>
                <code className="font-mono">{g.tool_name}</code>
                {g.reason && <span className="text-fg-subtle"> — {g.reason}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}
